import { useCallback, useEffect, useMemo, useState } from 'react'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported } from 'firebase/messaging'
import User from 'singletons/User'
import makeQuery from 'api/makeQuery'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
} as Record<string, string>

const isNotificationSupported = 'Notification' in window
const app = isNotificationSupported ? initializeApp(firebaseConfig) : undefined

const usePushService = () => {
  const [hasNotifPerm, setHasNotifPerm] = useState(false)
  const [isRegistered, setIsRegistered] = useState(false)

  // api is expecting mobile specific values, so setting a generic device for web
  const device = useMemo(
    () => ({
      make: 'web',
      model: 'web',
      modelVersion: 'web',
      platform: 'web',
      appVersion: 'web',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [],
  )

  const requestPermission = useCallback(async () => {
    const permission = await window.Notification?.requestPermission?.()
    setHasNotifPerm(permission === 'granted')
  }, [])

  const identifyServiceWorker = async () => {
    try {
      const sw = await navigator.serviceWorker?.getRegistration?.()
      if (sw) {
        sw.update()
        setIsRegistered(true)
      } else {
        setIsRegistered(false)
      }
    } catch (e) {
      setIsRegistered(false)
    }
  }

  const requestToken = useCallback(async () => {
    try {
      if (await isSupported()) {
        const messaging = getMessaging(app)
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        })
        if (token) {
          makeQuery('putPushRegistration', {
            providerId: User.providerId,
            registration: {
              device,
              token: {
                type: 'webfcm',
                data: token,
              },
            },
          })
        }
      }
    } catch (e) {
      console.log('An error occurred while registering for push notifications. ', e)
    }
  }, [User.providerId, device, location])

  useEffect(() => {
    if (User.providerId) {
      identifyServiceWorker()
    }
  }, [User.providerId])

  useEffect(() => {
    if (isNotificationSupported) {
      if (User.providerId) {
        if (!hasNotifPerm) {
          requestPermission()
        } else if (!isRegistered) {
          requestToken()
        }
      }
    }
  }, [User.providerId, requestToken, hasNotifPerm, isRegistered, requestPermission])
}

export default usePushService
